/**
 * Product service for API calls
 * @typedef {import('../models/Product').Product} Product
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Create a new product
 * @param {string} accessToken - User's access token
 * @param {Product} productData - Product data (name required)
 * @returns {Promise<{ success: boolean, product?: Product }>} Created product
 */
export const createProduct = async (accessToken, productData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/products`,
      { product: productData },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to create product');
  }
};

/**
 * Get all products for the current user
 * @param {string} accessToken - User's access token
 * @returns {Promise<{ products: Product[] }>} List of products
 */
export const getProducts = async (accessToken) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/products`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get products');
  }
};

/**
 * Get a single product by ID
 * @param {string} accessToken - User's access token
 * @param {string} productId - Product ID
 * @returns {Promise<{ success: boolean, product?: Product }>} Response with product data
 */
export const getProduct = async (accessToken, productId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/products/${productId}`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get product');
  }
};

/**
 * Update a product
 * @param {string} accessToken - User's access token
 * @param {string} productId - Product ID
 * @param {Partial<Product>} productData - Product update data (all fields optional)
 * @returns {Promise<{ success: boolean, product?: Product }>} Updated product
 */
export const updateProduct = async (accessToken, productId, productData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/products/${productId}`,
      { product: productData },
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to update product');
  }
};

/**
 * Delete a product
 * @param {string} accessToken - User's access token
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteProduct = async (accessToken, productId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/api/products/${productId}`, {
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to delete product');
  }
};
